import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, Calendar, Mail } from 'lucide-react';
import styles from './legal.module.css';
import livePulseLogo from '../assets/live-pulse-logo.png';

const LAST_UPDATED = 'March 12, 2026';
const CONTACT_EMAIL = 'info@ottosoft.in';
const COMPANY_NAME = 'OttoSoft';
const APP_NAME = 'LivePulse';

const SECTIONS = [
    { id: 'collect', num: '01', title: 'Information We Collect' },
    { id: 'use', num: '02', title: 'How We Use Your Information' },
    { id: 'sharing', num: '03', title: 'Information Sharing' },
    { id: 'retention', num: '04', title: 'Data Retention' },
    { id: 'cookies', num: '05', title: 'Cookies & Tracking' },
    { id: 'security', num: '06', title: 'Data Security' },
    { id: 'rights', num: '07', title: 'Your Rights' },
    { id: 'children', num: '08', title: "Children's Privacy" },
    { id: 'changes', num: '09', title: 'Changes to This Policy' },
    { id: 'contact', num: '10', title: 'Contact Us' },
];

export default function PrivacyPage() {
    const navigate = useNavigate();

    return (
        <div className={styles.page}>
            {/* Top bar */}
            <div className={styles.topBar}>
                <div className={styles.topBarInner}>
                    <Link to="/" className={styles.logoRow}>
                        <img src={livePulseLogo} alt="LivePulse" className={styles.logoBrand} />
                    </Link>
                    <button type="button" className={styles.backBtn} onClick={() => navigate(-1)}>
                        <ArrowLeft size={14} />
                        Back
                    </button>
                </div>
            </div>

            {/* Hero */}
            <div className={styles.hero}>
                <div className={styles.heroMesh} aria-hidden />
                <div className={styles.heroInner}>
                    <span className={styles.heroLabel}>Legal</span>
                    <h1 className={styles.heroTitle}>Privacy Policy</h1>
                    <div className={styles.heroBadges}>
                        <span className={styles.heroBadge}>
                            <ShieldCheck size={12} />
                            {APP_NAME} Platform
                        </span>
                        <span className={styles.heroBadge}>
                            <Calendar size={12} />
                            Last updated: {LAST_UPDATED}
                        </span>
                    </div>
                </div>
            </div>

            {/* Body */}
            <div className={styles.body}>
                {/* Intro */}
                <div className={styles.section} style={{ marginBottom: 0 }}>
                    <div className={styles.sectionBody}>
                        <p>
                            {COMPANY_NAME} ("we", "us", "our") operates {APP_NAME} and is committed to protecting
                            your personal information. This Privacy Policy explains what data we collect, how we
                            use it, and the choices you have regarding your information.
                        </p>
                        <p>
                            By using the Service, you agree to the collection and use of information as described
                            in this policy.
                        </p>
                    </div>
                </div>

                {/* Table of contents */}
                <nav className={styles.toc} aria-label="Table of contents" style={{ marginTop: 'var(--space-8)' }}>
                    <div className={styles.tocTitle}>Contents</div>
                    <ol className={styles.tocList}>
                        {SECTIONS.map((s) => (
                            <li key={s.id} className={styles.tocItem}>
                                <a href={`#${s.id}`}>
                                    <span className={styles.tocNum}>{s.num}</span>
                                    {s.title}
                                </a>
                            </li>
                        ))}
                    </ol>
                </nav>

                {/* Sections */}
                <section id="collect" className={styles.section}>
                    <div className={styles.sectionHead}>
                        <span className={styles.sectionNum}>1</span>
                        <h2 className={styles.sectionTitle}>Information We Collect</h2>
                    </div>
                    <div className={styles.sectionDivider} />
                    <div className={styles.sectionBody}>
                        <p><strong>Information you provide directly:</strong></p>
                        <ul>
                            <li><strong>Account information:</strong> Name, email address, and password when you register</li>
                            <li><strong>Profile information:</strong> Any additional details you add to your profile</li>
                            <li><strong>Event content:</strong> Questions, poll options, and other content you create</li>
                            <li><strong>Payment information:</strong> Billing details when purchasing a plan (processed securely; we do not store full card numbers)</li>
                            <li><strong>Communications:</strong> Messages you send us via the contact form or email</li>
                        </ul>
                        <p><strong>Information collected automatically:</strong></p>
                        <ul>
                            <li><strong>Usage data:</strong> Pages visited, features used, session duration, and interaction patterns</li>
                            <li><strong>Device information:</strong> Browser type, operating system, screen resolution, and language settings</li>
                            <li><strong>IP address and approximate location</strong> (country/city level only)</li>
                            <li><strong>Cookies and similar technologies</strong> — see Section 5</li>
                        </ul>
                        <p><strong>Participant data (no registration required):</strong></p>
                        <ul>
                            <li>Responses submitted by event participants are stored anonymously unless participants voluntarily provide their name</li>
                            <li>Participant IP addresses may be logged for abuse prevention</li>
                        </ul>
                    </div>
                </section>

                <section id="use" className={styles.section}>
                    <div className={styles.sectionHead}>
                        <span className={styles.sectionNum}>2</span>
                        <h2 className={styles.sectionTitle}>How We Use Your Information</h2>
                    </div>
                    <div className={styles.sectionDivider} />
                    <div className={styles.sectionBody}>
                        <p>We use the information we collect to:</p>
                        <ul>
                            <li>Provide, operate, and maintain the {APP_NAME} platform</li>
                            <li>Process registrations, authenticate users, and manage accounts</li>
                            <li>Process payments and send transaction-related emails</li>
                            <li>Send important service notices, such as account verification and password reset emails</li>
                            <li>Respond to your support requests and communications</li>
                            <li>Improve and personalise the Service based on usage patterns</li>
                            <li>Detect, prevent, and address fraud, abuse, or security incidents</li>
                            <li>Comply with applicable legal obligations</li>
                        </ul>
                        <p>
                            We do <strong>not</strong> sell your personal data to third parties. We do not use
                            your content to train AI models without your explicit consent.
                        </p>
                    </div>
                </section>

                <section id="sharing" className={styles.section}>
                    <div className={styles.sectionHead}>
                        <span className={styles.sectionNum}>3</span>
                        <h2 className={styles.sectionTitle}>Information Sharing</h2>
                    </div>
                    <div className={styles.sectionDivider} />
                    <div className={styles.sectionBody}>
                        <p>We may share your information only in the following circumstances:</p>
                        <ul>
                            <li>
                                <strong>Service providers:</strong> Trusted third-party vendors who help us operate
                                the Service (e.g., email delivery, payment processors, cloud hosting). These
                                providers are contractually obligated to protect your data and may only use it
                                for the purpose of providing services to us.
                            </li>
                            <li>
                                <strong>Legal requirements:</strong> If required by law, regulation, or legal
                                process, or to protect the rights, property, or safety of {COMPANY_NAME}, our
                                users, or the public.
                            </li>
                            <li>
                                <strong>Business transfers:</strong> In the event of a merger, acquisition, or
                                sale of assets, your data may be transferred as part of that transaction. We
                                will notify you before such a transfer and give you the option to delete your account.
                            </li>
                            <li>
                                <strong>With your consent:</strong> We may share information for other purposes
                                when you explicitly consent.
                            </li>
                        </ul>
                    </div>
                </section>

                <section id="retention" className={styles.section}>
                    <div className={styles.sectionHead}>
                        <span className={styles.sectionNum}>4</span>
                        <h2 className={styles.sectionTitle}>Data Retention</h2>
                    </div>
                    <div className={styles.sectionDivider} />
                    <div className={styles.sectionBody}>
                        <p>
                            We retain your personal data for as long as your account is active or as needed to
                            provide the Service. Specifically:
                        </p>
                        <ul>
                            <li>Account data is retained until you delete your account</li>
                            <li>Event data and poll results are retained while your account is active and for up to 90 days after account deletion</li>
                            <li>Payment records are retained for 7 years as required by Indian financial regulations</li>
                            <li>Log data (including IP addresses) is retained for up to 90 days for security purposes</li>
                        </ul>
                        <p>
                            You can delete your account at any time from the Settings page. Upon deletion, your
                            personal data and event content will be permanently removed within 30 days, except
                            where retention is required by law.
                        </p>
                    </div>
                </section>

                <section id="cookies" className={styles.section}>
                    <div className={styles.sectionHead}>
                        <span className={styles.sectionNum}>5</span>
                        <h2 className={styles.sectionTitle}>Cookies &amp; Tracking</h2>
                    </div>
                    <div className={styles.sectionDivider} />
                    <div className={styles.sectionBody}>
                        <p>
                            We use cookies and similar technologies to operate and improve the Service:
                        </p>
                        <ul>
                            <li><strong>Essential cookies:</strong> Required for authentication, session management, and core functionality. These cannot be disabled.</li>
                            <li><strong>Preference cookies:</strong> Remember your settings such as theme (light/dark mode) and language preferences.</li>
                            <li><strong>Analytics cookies:</strong> Help us understand how users interact with the Service. This data is aggregated and anonymised.</li>
                        </ul>
                        <p>
                            You can control cookies through your browser settings. Disabling essential cookies may
                            affect the functionality of the Service. We do not use advertising or third-party
                            tracking cookies.
                        </p>
                    </div>
                </section>

                <section id="security" className={styles.section}>
                    <div className={styles.sectionHead}>
                        <span className={styles.sectionNum}>6</span>
                        <h2 className={styles.sectionTitle}>Data Security</h2>
                    </div>
                    <div className={styles.sectionDivider} />
                    <div className={styles.sectionBody}>
                        <p>
                            We implement industry-standard security measures to protect your personal data, including:
                        </p>
                        <ul>
                            <li>Passwords are hashed using strong one-way cryptographic algorithms (bcrypt)</li>
                            <li>All data is transmitted over HTTPS (TLS encryption)</li>
                            <li>Authentication tokens are signed using secure cryptographic keys</li>
                            <li>Regular security reviews of our codebase and infrastructure</li>
                        </ul>
                        <p>
                            While we take reasonable steps to protect your information, no method of transmission
                            over the Internet or method of electronic storage is 100% secure. We cannot guarantee
                            absolute security. If you discover a security vulnerability, please report it
                            responsibly to <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
                        </p>
                    </div>
                </section>

                <section id="rights" className={styles.section}>
                    <div className={styles.sectionHead}>
                        <span className={styles.sectionNum}>7</span>
                        <h2 className={styles.sectionTitle}>Your Rights</h2>
                    </div>
                    <div className={styles.sectionDivider} />
                    <div className={styles.sectionBody}>
                        <p>You have the following rights regarding your personal data:</p>
                        <ul>
                            <li><strong>Access:</strong> Request a copy of the personal data we hold about you</li>
                            <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data</li>
                            <li><strong>Deletion:</strong> Request deletion of your personal data (subject to legal retention requirements)</li>
                            <li><strong>Portability:</strong> Request your data in a structured, machine-readable format</li>
                            <li><strong>Objection:</strong> Object to certain processing of your data</li>
                            <li><strong>Withdraw consent:</strong> Where processing is based on consent, withdraw it at any time</li>
                        </ul>
                        <p>
                            To exercise these rights, contact us at <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
                            We will respond to your request within 30 days.
                        </p>
                    </div>
                </section>

                <section id="children" className={styles.section}>
                    <div className={styles.sectionHead}>
                        <span className={styles.sectionNum}>8</span>
                        <h2 className={styles.sectionTitle}>Children's Privacy</h2>
                    </div>
                    <div className={styles.sectionDivider} />
                    <div className={styles.sectionBody}>
                        <p>
                            The {APP_NAME} platform is not directed to children under the age of 13. We do not
                            knowingly collect personal information from children under 13.
                        </p>
                        <p>
                            If you are a parent or guardian and believe your child has provided us with personal
                            data, please contact us at <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> and
                            we will promptly delete such information.
                        </p>
                    </div>
                </section>

                <section id="changes" className={styles.section}>
                    <div className={styles.sectionHead}>
                        <span className={styles.sectionNum}>9</span>
                        <h2 className={styles.sectionTitle}>Changes to This Policy</h2>
                    </div>
                    <div className={styles.sectionDivider} />
                    <div className={styles.sectionBody}>
                        <p>
                            We may update this Privacy Policy from time to time. When we make significant changes,
                            we will update the "Last updated" date at the top of this page and notify registered
                            users by email.
                        </p>
                        <p>
                            We encourage you to review this policy periodically. Your continued use of the Service
                            after changes are posted constitutes your acceptance of the revised Privacy Policy.
                        </p>
                    </div>
                </section>

                <section id="contact" className={styles.section}>
                    <div className={styles.sectionHead}>
                        <span className={styles.sectionNum}>10</span>
                        <h2 className={styles.sectionTitle}>Contact Us</h2>
                    </div>
                    <div className={styles.sectionDivider} />
                    <div className={styles.sectionBody}>
                        <p>
                            If you have questions, concerns, or requests about this Privacy Policy or your
                            personal data, please contact our team:
                        </p>
                        <div className={styles.contactBox}>
                            <p><strong>{COMPANY_NAME} — Data Controller</strong></p>
                            <p>
                                <Mail size={13} style={{ display: 'inline', marginRight: 6, verticalAlign: 'middle' }} />
                                Email: <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
                            </p>
                            <p>
                                Address: 1st Floor, Shekhar Complex Khoja Imli Mazar,
                                Phulwarisarif, Patna-801505, Bihar, India
                            </p>
                        </div>
                    </div>
                </section>
            </div>

            {/* Footer */}
            <footer className={styles.footer}>
                <div className={styles.footerInner}>
                    <span className={styles.footerText}>© {new Date().getFullYear()} {COMPANY_NAME}. All rights reserved.</span>
                    <div className={styles.footerLinks}>
                        <Link to="/terms">Terms of Service</Link>
                        <Link to="/privacy">Privacy Policy</Link>
                        <Link to="/">Home</Link>
                    </div>
                </div>
            </footer>
        </div>
    );
}
